import React, { useEffect, useState } from "react";
import { Star, TrendingUp, MessageSquare, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fetchLawyerReviews } from "@lawyer/services/lawyerApi";

const STAR_BUCKETS = ["5", "4", "3", "2", "1"];

export default function LawyerReviews() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE = 10;

  useEffect(() => {
    fetchLawyerReviews({ skip: page * PAGE, limit: PAGE })
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load reviews"));
  }, [page]);

  if (!data) return <div className="text-slate-500" data-testid="lawyer-reviews-loading">Loading reviews…</div>;

  const { totals, items, total } = data;
  const trendDelta = (totals.recent_30d || 0) - (totals.prior_30d || 0);

  return (
    <>
      <div className="mb-6" data-testid="lawyer-reviews-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Reviews & Ratings</h1>
        <p className="mt-1 text-sm text-slate-500">What your clients are saying — only approved reviews are shown.</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#0A2540] text-white rounded-2xl p-5" data-testid="reviews-overall-rating">
          <p className="text-[11px] uppercase tracking-widest font-bold opacity-70">Overall rating</p>
          <div className="flex items-baseline gap-3 mt-2">
            <p className="text-5xl font-extrabold" style={{ fontFamily: "Poppins" }}>{(totals.avg_rating || 0).toFixed(1)}</p>
            <div className="flex">{[1,2,3,4,5].map((i) => (
              <Star key={i} size={14} className={i <= Math.round(totals.avg_rating || 0) ? "text-[#D4AF37]" : "text-white/30"} fill="currentColor" />
            ))}</div>
          </div>
          <p className="mt-1 text-xs opacity-80">From {total} review{total === 1 ? "" : "s"}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="reviews-distribution">
          <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Distribution</p>
          <div className="mt-2 space-y-1">
            {STAR_BUCKETS.map((s) => {
              const count = totals.distribution?.[s] || 0;
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={s} className="flex items-center gap-2 text-xs">
                  <span className="w-6 font-bold text-slate-700">{s}★</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-[10px] text-slate-500">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="reviews-trend">
          <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Trend (last 30 days)</p>
          <p className="mt-2 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{totals.recent_30d || 0}</p>
          <p className={`mt-1 text-xs flex items-center gap-1 font-bold ${trendDelta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            <TrendingUp size={12} className={trendDelta >= 0 ? "" : "rotate-180"} />
            {trendDelta >= 0 ? "+" : ""}{trendDelta} vs prior 30 days
          </p>
        </div>
      </div>

      {/* Review list */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="reviews-list">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            <MessageSquare size={28} className="mx-auto text-slate-300 mb-2" />
            No reviews yet. Complete consultations and ask clients to share feedback.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((r) => (
              <li key={r.id || r.consultation_id} className="p-4" data-testid={`review-item-${r.id || r.consultation_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-[#0A2540] text-sm">{r.user_name || "Client"}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} size={12} className={i <= (r.rating || 0) ? "text-[#D4AF37]" : "text-slate-200"} fill="currentColor" />
                      ))}
                      <span className="ml-2 text-[10px] text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                  {r.featured && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5">Featured</span>
                  )}
                </div>
                {r.text && <p className="mt-2 text-sm text-slate-600">"{r.text}"</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {total > PAGE && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>Page {page + 1} of {Math.ceil(total / PAGE)}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded-md border border-slate-200 disabled:opacity-40"
              data-testid="reviews-prev-btn">
              Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * PAGE >= total}
              className="px-2 py-1 rounded-md border border-slate-200 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="reviews-next-btn">
              Next <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
